import { useCallback, useEffect, useState } from "react";
import API, { formatApiError } from "../../lib/api";
import { Button } from "../ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from "../ui/dialog";
import { Textarea } from "../ui/textarea";
import { Checkbox } from "../ui/checkbox";
import { Label } from "../ui/label";
import { CalendarPlus, XCircle } from "@phosphor-icons/react";
import { toast } from "sonner";
import { LeaveCalendar } from "./LeaveCalendar";
import { StatusPill, dayRange } from "./hrUtils";

const Stat = ({ label, value, tone = "", testId }) => (
  <div className="rounded-lg border border-border bg-card px-3 py-2">
    <p className="text-[10px] uppercase tracking-[0.18em] text-muted-foreground font-semibold">{label}</p>
    <p className={`text-xl font-bold tabular-nums ${tone}`} data-testid={testId}>{value}</p>
  </div>
);

/** Annual leave: allowance, team calendar with blocked dates, request dialog, own requests. */
export const LeaveTab = ({ userId, isOps, isSelf, onChanged }) => {
  const [data, setData] = useState({ leave: [], summary: null });
  const [open, setOpen] = useState(false);
  const [range, setRange] = useState({ start: null, end: null });
  const [note, setNote] = useState("");
  const [half, setHalf] = useState(false);
  const [force, setForce] = useState(false);
  const [busy, setBusy] = useState(false);
  const [refresh, setRefresh] = useState(0);
  const year = new Date().getFullYear();

  const load = useCallback(async () => {
    try { const { data: d } = await API.get(`/hr/leave?year=${year}${userId ? `&user_id=${userId}` : ""}`); setData(d); } catch (e) { toast.error(formatApiError(e)); }
  }, [userId, year]);
  useEffect(() => { load(); }, [load]);

  const submit = async () => {
    if (!range.start) return toast.error("Pick your dates on the calendar");
    setBusy(true);
    try {
      const body = { start_date: range.start, end_date: range.end || range.start, note, half_day: half, user_id: userId || undefined, force: isOps && force };
      const { data: r } = await API.post("/hr/leave", body);
      toast.success(r.status === "approved" ? "Leave booked" : "Leave requested — an Operations Director will review it");
      setOpen(false); setRange({ start: null, end: null }); setNote(""); setHalf(false); setForce(false);
      setRefresh((x) => x + 1); load(); onChanged?.();
    } catch (e) {
      const det = e.response?.data?.detail;
      toast.error(det?.message || formatApiError(e), det?.blocked_dates ? { description: `Taken: ${det.blocked_dates.map((d) => d.slice(8, 10) + "/" + d.slice(5, 7)).join(", ")}` } : undefined);
    } finally { setBusy(false); }
  };

  const cancel = async (l) => {
    if (!window.confirm(`Cancel leave ${dayRange(l.start_date, l.end_date)}?`)) return;
    try { await API.post(`/hr/leave/${l.id}/cancel`, {}); toast.success("Leave cancelled"); setRefresh((x) => x + 1); load(); onChanged?.(); } catch (e) { toast.error(formatApiError(e)); }
  };

  const s = data.summary || {};
  const single = range.start && (!range.end || range.end === range.start);
  return (
    <div className="space-y-5" data-testid="hr-leave-tab">
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
        <Stat label="Allowance" value={`${s.allowance ?? "—"}d`} testId="hr-leave-allowance" />
        <Stat label="Approved" value={`${s.approved ?? 0}d`} tone="text-emerald-700" testId="hr-leave-approved" />
        <Stat label="Pending" value={`${s.pending ?? 0}d`} tone="text-amber-700" testId="hr-leave-pending" />
        <Stat label="Remaining" value={`${s.remaining ?? "—"}d`} tone={s.remaining < 0 ? "text-red-600" : ""} testId="hr-leave-remaining" />
      </div>
      <div className="grid lg:grid-cols-[minmax(0,1fr)_minmax(0,1fr)] gap-5 items-start">
        <div className="space-y-3">
          <LeaveCalendar value={null} selectable={false} refreshKey={refresh} />
          <Button className="w-full" onClick={() => setOpen(true)} data-testid="hr-request-leave-btn"><CalendarPlus className="h-4 w-4 mr-2" /> {isSelf ? "Request annual leave" : "Book leave for this person"}</Button>
          <p className="text-xs text-muted-foreground">One person off at a time: dates someone has already requested or booked are blocked for everyone else, first come first served.</p>
        </div>
        <div className="rounded-xl border border-border divide-y divide-border">
          <p className="px-3 py-2 text-xs uppercase tracking-[0.18em] font-semibold text-muted-foreground">{year} requests</p>
          {data.leave.length === 0 && <p className="p-4 text-sm text-muted-foreground">No leave requested this year.</p>}
          {data.leave.map((l) => (
            <div key={l.id} className="flex items-center justify-between gap-3 px-3 py-2.5" data-testid={`hr-leave-row-${l.id}`}>
              <div className="min-w-0">
                <p className="text-sm font-medium">{dayRange(l.start_date, l.end_date)} <span className="text-muted-foreground font-normal">· {l.days}d</span></p>
                <p className="text-xs text-muted-foreground truncate">{l.note || (l.decision_note ? `${l.decided_by}: ${l.decision_note}` : l.status === "pending" ? "Awaiting an Operations Director" : "")}</p>
              </div>
              <div className="flex items-center gap-2 shrink-0">
                <StatusPill status={l.status} />
                {["pending", "approved"].includes(l.status) && l.end_date >= new Date().toISOString().slice(0, 10) && (
                  <button className="text-muted-foreground hover:text-destructive" title="Cancel" onClick={() => cancel(l)} data-testid={`hr-leave-cancel-${l.id}`}><XCircle size={18} /></button>
                )}
              </div>
            </div>
          ))}
        </div>
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>{isSelf ? "Request annual leave" : "Book annual leave"}</DialogTitle>
            <DialogDescription>Tap the first day, then the last day. Hatched days are taken.</DialogDescription>
          </DialogHeader>
          <LeaveCalendar value={range} onChange={setRange} refreshKey={refresh} />
          <div className="text-sm" data-testid="hr-leave-selected">
            {range.start ? <>Selected: <strong>{dayRange(range.start, range.end || range.start)}</strong></> : <span className="text-muted-foreground">No dates selected</span>}
          </div>
          <div className="space-y-3">
            {single && <label className="flex items-center gap-2 text-sm"><Checkbox checked={half} onCheckedChange={(v) => setHalf(!!v)} data-testid="hr-leave-half" /> Half day</label>}
            <div><Label>Note (optional)</Label><Textarea rows={2} value={note} onChange={(e) => setNote(e.target.value)} placeholder="e.g. Family holiday" data-testid="hr-leave-note" /></div>
            {isOps && <label className="flex items-center gap-2 text-sm text-amber-800"><Checkbox checked={force} onCheckedChange={(v) => setForce(!!v)} data-testid="hr-leave-force" /> Override — book even if dates are taken or over allowance</label>}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)}>Cancel</Button>
            <Button onClick={submit} disabled={busy || !range.start} data-testid="hr-leave-submit">{isSelf && !isOps ? "Send request" : "Book leave"}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};
